import { useEffect, useMemo, useRef, useState } from "react";

import {
  Box,
  CircularProgress,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";

import * as echarts from "echarts";
import { v4 as uuidv4 } from "uuid";

import CustomMenu from "components/Menu";

import useAuthScope from "hooks/useAuthScope";
import useDataSourceManager from "hooks/useDataSourceManager";

import { SolidButton } from "components/buttons/SolidButton";
import { OutlineButton } from "components/buttons/OutlineButton";

const COLORS = {
  primary: "#1F3173",
  secondary: "#8CAAFB",
  lightBlue: "#B9C8F5",

  text: "#07164D",
  mutedText: "#8A96B3",

  border: "#DCE3F0",
  grid: "#E5E7EB",

  background: "#FFFFFF",
  softBackground: "#FAFBFD",
  selectedBackground: "#F5F7FC",
};

const WEEK_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const formatLinkRate = (value) => {
  if (value == null) {
    return "-";
  }

  const numberValue = Number(value);

  if (!Number.isFinite(numberValue)) {
    return "-";
  }

  const Gbps = 1000000000;
  const Mbps = 1000000;
  const Kbps = 1000;

  if (numberValue >= Gbps) {
    return `${(numberValue / Gbps).toFixed(1)} Gbps`;
  }

  if (numberValue >= Mbps) {
    return `${(numberValue / Mbps).toFixed(1)} Mbps`;
  }

  if (numberValue >= Kbps) {
    return `${(numberValue / Kbps).toFixed(1)} Kbps`;
  }

  return `${numberValue} bps`;
};

const formatPoePower = (value) => {
  if (value == null) {
    return "-";
  }

  const numberValue = Number(value);

  if (!Number.isFinite(numberValue)) {
    return "-";
  }

  return `${numberValue.toFixed(1)} W`;
};

const formatPercent = (value) => {
  if (value == null) {
    return "-";
  }

  const numberValue = Number(value);

  if (!Number.isFinite(numberValue)) {
    return "-";
  }

  return `${numberValue.toFixed(1)}%`;
};

const FilterButton = ({ value, onClick, open, width = "165px" }) => {
  return (
    <Box
      onClick={onClick}
      sx={{
        width,

        height: "34px",

        px: "10px",

        display: "flex",

        alignItems: "center",

        justifyContent: "space-between",

        boxSizing: "border-box",

        border: open
          ? `1px solid ${COLORS.primary}`
          : `1px solid ${COLORS.border}`,

        borderRadius: "6px",

        backgroundColor: "#FFFFFF",

        cursor: "pointer",

        transition: "0.15s",

        "&:hover": {
          borderColor: COLORS.primary,
        },
      }}
    >
      <Typography
        sx={{
          flex: 1,

          minWidth: 0,

          fontSize: "12px",

          color: COLORS.text,

          overflow: "hidden",

          textOverflow: "ellipsis",

          whiteSpace: "nowrap",
        }}
      >
        {value}
      </Typography>

      <Box
        component="span"
        sx={{
          marginLeft: "8px",

          width: 0,

          height: 0,

          borderLeft: "4px solid transparent",

          borderRight: "4px solid transparent",

          borderTop: open ? "none" : "5px solid #8A96B3",

          borderBottom: open ? "5px solid #8A96B3" : "none",

          flexShrink: 0,
        }}
      />
    </Box>
  );
};

const WiredInsight = ({ timeRange }) => {
  const { auth, standalone, isAuthReady } = useAuthScope();
  const { sendBatch } = useDataSourceManager();
  const [switchValue, setSwitchValue] = useState("All");
  const [portValue, setPortValue] = useState("None");
  const [switchOptions, setSwitchOptions] = useState([]);
  const [portOptions, setPortOptions] = useState([]);
  const [switchAnchorEl, setSwitchAnchorEl] = useState(null);
  const [portAnchorEl, setPortAnchorEl] = useState(null);
  const [activePortThroughput, setActivePortThroughput] = useState([]);
  const [linkRate, setLinkRate] = useState([]);
  const [topPortsByTraffic, setTopPortsByTraffic] = useState([]);
  const [portList, setPortList] = useState([]);
  const [selectedPort, setSelectedPort] = useState(null);
  const [loading, setLoading] = useState(true);
  const summaryReportId = timeRange?.summaryReportId;
  const entId = standalone ? null : auth.realm?.id;
  const orgId = standalone ? auth.realm?.id : auth.orgs?.id;
  const siteId = auth.sites?.id;

  useEffect(() => {
    if (!isAuthReady) {
      return;
    }

    if (!summaryReportId || !orgId || !siteId) {
      setLoading(false);

      return;
    }

    const apiKey = standalone
      ? "/orgs/:orgId/sites/:siteId/summaryReports/:summaryReportId/wiredInsight.read"
      : "/ents/:entId/orgs/:orgId/sites/:siteId/summaryReports/:summaryReportId/wiredInsight.read";

    const restfulPlaceholders = standalone
      ? {
          orgId,
          siteId,
          summaryReportId,
        }
      : {
          entId,
          orgId,
          siteId,
          summaryReportId,
        };

    const load = async () => {
      const requestUuid = uuidv4();

      try {
        setLoading(true);

        const requestData = {
          ...(switchValue !== "All"
            ? {
                deviceId: switchValue,
              }
            : {}),

          ...(portValue !== "None"
            ? {
                portIndex: Number(portValue),
              }
            : {}),
        };

        const { getSendBatchData } = await sendBatch([
          {
            apiKey,

            requestUuid,

            restfulPlaceholders,

            params: {
              data: requestData,
            },
          },
        ]);

        const response = getSendBatchData({
          requestUuid,
        });

        const data = response?.result?.data ?? response?.data ?? {};

        const filters = data?.filters ?? {};

        setSwitchOptions(
          Array.isArray(filters.switches) ? filters.switches : [],
        );

        setPortOptions(Array.isArray(filters.ports) ? filters.ports : []);

        setActivePortThroughput(
          Array.isArray(data.activePortThroughput)
            ? data.activePortThroughput.map((item) => {
                if (!item) {
                  return null;
                }

                return {
                  throughput:
                    item.throughput != null ? Number(item.throughput) : null,

                  activePorts:
                    item.activePorts != null ? Number(item.activePorts) : null,

                  errorPorts:
                    item.errorPorts != null ? Number(item.errorPorts) : null,
                };
              })
            : [],
        );

        setLinkRate(
          Array.isArray(data.linkRate)
            ? data.linkRate.map((item) => (item != null ? Number(item) : null))
            : [],
        );

        setTopPortsByTraffic(
          Array.isArray(data.topPortsByTraffic)
            ? data.topPortsByTraffic.map((item) => ({
                ...item,

                portIndex: Number(item.portIndex),

                value: Number(item.value ?? 0),
              }))
            : [],
        );

        const nextPortList = Array.isArray(data.portList)
          ? data.portList.map((item) => ({
              ...item,

              portIndex: Number(item.portIndex),

              linkRate: item.linkRate != null ? Number(item.linkRate) : null,

              utilizationPercent:
                item.utilizationPercent != null
                  ? Number(item.utilizationPercent)
                  : null,

              errorCount:
                item.errorCount != null ? Number(item.errorCount) : null,

              poePower: item.poePower != null ? Number(item.poePower) : null,
            }))
          : [];
        //預設選第一筆資料
        setPortList(nextPortList);

        setSelectedPort((previous) => {
          if (nextPortList.length === 0) {
            return null;
          }

          if (!previous) {
            return nextPortList[0];
          }

          const stillExists = nextPortList.find(
            (item) =>
              item.deviceId === previous.deviceId &&
              Number(item.portIndex) === Number(previous.portIndex),
          );

          return stillExists ?? nextPortList[0];
        });

        setSelectedPort((previous) => {
          if (!previous) {
            return null;
          }

          return (
            nextPortList.find(
              (item) =>
                item.deviceId === previous.deviceId &&
                Number(item.portIndex) === Number(previous.portIndex),
            ) ?? null
          );
        });
      } catch (error) {
        console.error(">>> [WiredInsight] load error:", error);

        setActivePortThroughput([]);

        setLinkRate([]);

        setTopPortsByTraffic([]);

        setPortList([]);

        setSelectedPort(null);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [
    entId,
    isAuthReady,
    orgId,
    portValue,
    sendBatch,
    siteId,
    standalone,
    summaryReportId,
    switchValue,
  ]);

  useEffect(() => {
    setSwitchValue("All");

    setPortValue("None");

    setSelectedPort(null);

    setSwitchAnchorEl(null);

    setPortAnchorEl(null);
  }, [summaryReportId]);

  const dates = WEEK_DAYS;

  const activePortOption = useMemo(() => {
    const normalized = WEEK_DAYS.map(
      (_, index) => activePortThroughput[index] ?? null,
    );

    const throughputData = normalized.map((item) =>
      item?.throughput != null ? Number(item.throughput) / 1000000 : null,
    );

    const activePortsData = normalized.map((item) =>
      item?.activePorts != null ? Number(item.activePorts) : null,
    );

    const errorPortsData = normalized.map((item) =>
      item?.errorPorts != null ? Number(item.errorPorts) : null,
    );

    const stackedPortData = activePortsData.map((activePort, index) => {
      const active = Number.isFinite(activePort) ? activePort : 0;

      const error = Number.isFinite(errorPortsData[index])
        ? errorPortsData[index]
        : 0;

      return active + error;
    });

    const maxPorts =
      stackedPortData.length > 0 ? Math.max(...stackedPortData) : 0;

    const portsAxisMax = Math.max(
      20,

      Math.ceil(maxPorts / 20) * 20 + 20,
    );

    const validThroughput = throughputData.filter(Number.isFinite);

    const maxThroughput =
      validThroughput.length > 0 ? Math.max(...validThroughput) : 0;

    const throughputAxisMax = Math.max(
      100,

      Math.ceil(maxThroughput / 100) * 100,
    );

    return {
      animationDuration: 500,

      tooltip: {
        trigger: "axis",

        backgroundColor: "#FFFFFF",

        borderColor: COLORS.border,

        borderWidth: 1,

        padding: [8, 10],

        textStyle: {
          color: COLORS.text,

          fontSize: 12,
        },

        axisPointer: {
          type: "shadow",

          shadowStyle: {
            color: "rgba(31, 49, 115, 0.04)",
          },
        },
      },

      legend: {
        bottom: 4,

        left: 18,

        itemWidth: 9,

        itemHeight: 9,

        itemGap: 18,

        icon: "circle",

        textStyle: {
          fontSize: 11,

          color: "#7382A5",
        },

        data: ["Throughput (Mbps)", "Active Ports", "Error Ports"],
      },

      grid: {
        top: 38,

        left: 60,

        right: 60,

        bottom: 55,

        containLabel: false,
      },

      xAxis: {
        type: "category",

        data: dates,

        axisTick: {
          show: false,
        },

        axisLabel: {
          fontSize: 11,

          color: "#8A90A0",

          margin: 10,
        },

        axisLine: {
          show: true,

          lineStyle: {
            color: COLORS.border,

            width: 1,
          },
        },

        splitLine: {
          show: true,

          lineStyle: {
            color: COLORS.grid,

            width: 1,

            type: "dashed",
          },
        },
      },

      yAxis: [
        {
          type: "value",

          name: "Ports",

          min: 0,

          max: portsAxisMax,

          splitNumber: 5,

          nameGap: 12,

          nameTextStyle: {
            fontSize: 11,

            color: "#8A90A0",
          },

          axisLabel: {
            fontSize: 10,

            color: "#8A90A0",
          },

          axisLine: {
            show: false,
          },

          axisTick: {
            show: false,
          },

          splitLine: {
            show: true,

            lineStyle: {
              color: COLORS.grid,

              width: 1,

              type: "dashed",
            },
          },
        },

        {
          type: "value",

          name: "Mbps",

          min: 0,

          max: throughputAxisMax,

          splitNumber: 5,

          nameGap: 12,

          nameTextStyle: {
            fontSize: 11,

            color: "#8A90A0",
          },

          axisLabel: {
            fontSize: 10,

            color: "#8A90A0",
          },

          axisLine: {
            show: false,
          },

          axisTick: {
            show: false,
          },

          splitLine: {
            show: false,
          },
        },
      ],

      series: [
        {
          name: "Throughput (Mbps)",

          type: "line",

          yAxisIndex: 1,

          data: throughputData,

          smooth: true,

          showSymbol: true,

          symbol: "circle",

          symbolSize: 5,

          connectNulls: false,

          lineStyle: {
            width: 2,

            color: COLORS.primary,
          },

          itemStyle: {
            color: COLORS.primary,
          },

          emphasis: {
            disabled: true,
          },

          z: 10,
        },

        {
          name: "Active Ports",

          type: "bar",

          stack: "ports",

          yAxisIndex: 0,

          data: activePortsData,

          barWidth: 24,

          itemStyle: {
            color: COLORS.primary,
          },

          emphasis: {
            disabled: true,
          },

          z: 2,
        },

        {
          name: "Error Ports",

          type: "bar",

          stack: "ports",

          yAxisIndex: 0,

          data: errorPortsData,

          barWidth: 24,

          itemStyle: {
            color: COLORS.secondary,

            borderRadius: [3, 3, 0, 0],
          },

          emphasis: {
            disabled: true,
          },

          z: 3,
        },
      ],
    };
  }, [activePortThroughput, dates]);

  const performanceOption = useMemo(() => {
    const normalized = WEEK_DAYS.map((_, index) => linkRate[index] ?? null);

    const linkRateData = normalized.map((item) =>
      item != null ? Number(item) / 1000000000 : null,
    );

    const validValues = linkRateData.filter(Number.isFinite);

    const maxValue = validValues.length > 0 ? Math.max(...validValues) : 0;

    const axisMax = Math.max(
      10,

      Math.ceil(maxValue / 10) * 10,
    );

    return {
      animationDuration: 500,

      tooltip: {
        trigger: "axis",

        backgroundColor: "#FFFFFF",

        borderColor: COLORS.border,

        borderWidth: 1,

        padding: [8, 10],

        textStyle: {
          color: COLORS.text,

          fontSize: 12,
        },
      },

      legend: {
        bottom: 4,

        left: 18,

        itemWidth: 9,

        itemHeight: 9,

        icon: "circle",

        data: ["Sum (Linkrate) Gbps"],
      },

      grid: {
        top: 38,

        left: 60,

        right: 30,

        bottom: 55,
      },

      xAxis: {
        type: "category",

        boundaryGap: false,

        data: dates,

        axisTick: {
          show: false,
        },

        axisLabel: {
          fontSize: 11,

          color: "#8A90A0",
        },

        axisLine: {
          lineStyle: {
            color: COLORS.border,
          },
        },

        splitLine: {
          show: true,

          lineStyle: {
            color: COLORS.grid,

            type: "dashed",
          },
        },
      },

      yAxis: {
        type: "value",

        name: "Gbps",

        min: 0,

        max: axisMax,

        splitNumber: 5,

        axisLine: {
          show: false,
        },

        axisTick: {
          show: false,
        },

        splitLine: {
          show: true,

          lineStyle: {
            color: COLORS.grid,

            type: "dashed",
          },
        },
      },

      series: [
        {
          name: "Sum (Linkrate) Gbps",

          type: "line",

          data: linkRateData,

          smooth: true,

          showSymbol: true,

          symbol: "circle",

          symbolSize: 5,

          connectNulls: false,

          lineStyle: {
            width: 2,

            color: COLORS.primary,
          },

          itemStyle: {
            color: COLORS.primary,
          },

          areaStyle: {
            color: {
              type: "linear",

              x: 0,

              y: 0,

              x2: 0,

              y2: 1,

              colorStops: [
                {
                  offset: 0,

                  color: "rgba(31,49,115,0.15)",
                },

                {
                  offset: 1,

                  color: "rgba(31,49,115,0.02)",
                },
              ],
            },
          },
        },
      ],
    };
  }, [dates, linkRate]);

  const trafficData = useMemo(() => {
    return topPortsByTraffic.slice(0, 5).map((item) => ({
      ...item,

      name: `${item.switchName ?? "-"} - ${
        item.portName ?? `Port ${item.portIndex}`
      }`,

      value: Number(item.value ?? 0),
    }));
  }, [topPortsByTraffic]);

  const trafficOption = useMemo(() => {
    const maxValue =
      trafficData.length > 0
        ? Math.max(...trafficData.map((item) => item.value))
        : 0;

    return {
      animationDuration: 500,

      tooltip: {
        trigger: "axis",

        backgroundColor: "#FFFFFF",

        borderColor: COLORS.border,

        borderWidth: 1,

        axisPointer: {
          type: "shadow",
        },
      },

      grid: {
        top: 10,

        left: 145,

        right: 70,

        bottom: 10,
      },

      xAxis: {
        type: "value",

        max: maxValue > 0 ? maxValue * 1.25 : 1,

        show: false,
      },

      yAxis: {
        type: "category",

        inverse: true,

        data: trafficData.map((item) => item.name),

        axisLine: {
          show: false,
        },

        axisTick: {
          show: false,
        },

        axisLabel: {
          color: COLORS.text,

          fontSize: 11,
        },
      },

      series: [
        {
          type: "bar",

          data: trafficData.map((item) => item.value),

          barWidth: 11,

          itemStyle: {
            color: COLORS.primary,

            borderRadius: [0, 3, 3, 0],
          },

          label: {
            show: true,

            position: "right",

            color: COLORS.text,

            fontSize: 11,
          },
        },
      ],
    };
  }, [trafficData]);

  const currentSwitchName = useMemo(() => {
    if (switchValue === "All") {
      return "All";
    }

    return (
      switchOptions.find((item) => item.deviceId === switchValue)?.switchName ??
      "-"
    );
  }, [switchOptions, switchValue]);

  const currentPortName = useMemo(() => {
    if (portValue === "None") {
      return "None (Aggregate)";
    }

    const target = portOptions.find(
      (item) => Number(item.portIndex) === Number(portValue),
    );

    return target?.portName ?? `Port ${portValue}`;
  }, [portOptions, portValue]);

  const handleSwitchMenuOpen = (event) => {
    event.stopPropagation();

    setSwitchAnchorEl(event.currentTarget);

    setPortAnchorEl(null);
  };

  const handleSwitchMenuClose = () => {
    setSwitchAnchorEl(null);
  };

  const handleSwitchChange = (deviceId) => {
    setSelectedPort(null);

    setSwitchValue(deviceId);

    setPortValue("None");

    setSwitchAnchorEl(null);
  };

  const handlePortMenuOpen = (event) => {
    event.stopPropagation();

    setPortAnchorEl(event.currentTarget);

    setSwitchAnchorEl(null);
  };

  const handlePortMenuClose = () => {
    setPortAnchorEl(null);
  };

  const handlePortChange = (value) => {
    setSelectedPort(null);

    setPortValue(value);

    setPortAnchorEl(null);
  };

  const handleSelectPort = (item) => {
    setSelectedPort(item);

    setSwitchValue(item.deviceId);

    setPortValue(item.portIndex);
  };

  const handleClear = () => {
    setSelectedPort(null);

    setSwitchValue("All");

    setPortValue("None");
  };

  return (
    <Box
      sx={{
        width: "100%",

        display: "flex",

        gap: "16px",

        backgroundColor: "#FFFFFF",

        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          width: "44%",

          minWidth: "420px",

          padding: "16px",

          backgroundColor: "#FFFFFF",

          border: `1px solid ${COLORS.border}`,

          borderRadius: "6px",

          boxSizing: "border-box",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{
            marginBottom: "18px",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography
              sx={{
                fontSize: "12px",

                fontWeight: 500,

                color: COLORS.text,

                whiteSpace: "nowrap",
              }}
            >
              Switch
            </Typography>

            <Box>
              <FilterButton
                value={currentSwitchName}
                open={Boolean(switchAnchorEl)}
                onClick={handleSwitchMenuOpen}
                width="165px"
              />

              <CustomMenu
                anchorEl={switchAnchorEl}
                handleMenuClose={handleSwitchMenuClose}
                anchorHorizontal="left"
                transformHorizontal="left"
                mt={0.5}
                minWidth="165px"
                maxWidth="260px"
              >
                <MenuItem
                  onClick={() => handleSwitchChange("All")}
                  sx={{
                    minHeight: "36px",

                    fontSize: "12px",

                    color: switchValue === "All" ? COLORS.primary : COLORS.text,

                    fontWeight: switchValue === "All" ? 600 : 400,

                    backgroundColor:
                      switchValue === "All"
                        ? COLORS.selectedBackground
                        : "transparent",

                    "&:hover": {
                      backgroundColor: COLORS.selectedBackground,
                    },
                  }}
                >
                  All
                </MenuItem>

                {switchOptions.map((item) => {
                  const selected = switchValue === item.deviceId;

                  return (
                    <MenuItem
                      key={item.deviceId}
                      onClick={() => handleSwitchChange(item.deviceId)}
                      sx={{
                        minHeight: "36px",

                        fontSize: "12px",

                        color: selected ? COLORS.primary : COLORS.text,

                        fontWeight: selected ? 600 : 400,

                        backgroundColor: selected
                          ? COLORS.selectedBackground
                          : "transparent",

                        "&:hover": {
                          backgroundColor: COLORS.selectedBackground,
                        },
                      }}
                    >
                      {item.switchName ?? item.deviceId}
                    </MenuItem>
                  );
                })}
              </CustomMenu>
            </Box>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography
              sx={{
                fontSize: "12px",

                fontWeight: 500,

                color: COLORS.text,

                whiteSpace: "nowrap",
              }}
            >
              Port
            </Typography>

            <Box>
              <FilterButton
                value={currentPortName}
                open={Boolean(portAnchorEl)}
                onClick={handlePortMenuOpen}
                width="165px"
              />

              <CustomMenu
                anchorEl={portAnchorEl}
                handleMenuClose={handlePortMenuClose}
                anchorHorizontal="left"
                transformHorizontal="left"
                mt={0.5}
                minWidth="165px"
                maxWidth="260px"
              >
                <MenuItem
                  onClick={() => handlePortChange("None")}
                  sx={{
                    minHeight: "36px",

                    fontSize: "12px",

                    color: portValue === "None" ? COLORS.primary : COLORS.text,

                    fontWeight: portValue === "None" ? 600 : 400,

                    backgroundColor:
                      portValue === "None"
                        ? COLORS.selectedBackground
                        : "transparent",

                    "&:hover": {
                      backgroundColor: COLORS.selectedBackground,
                    },
                  }}
                >
                  None (Aggregate)
                </MenuItem>

                {portOptions.map((item) => {
                  const selected = Number(portValue) === Number(item.portIndex);

                  return (
                    <MenuItem
                      key={item.portIndex}
                      onClick={() => handlePortChange(item.portIndex)}
                      sx={{
                        minHeight: "36px",

                        fontSize: "12px",

                        color: selected ? COLORS.primary : COLORS.text,

                        fontWeight: selected ? 600 : 400,

                        backgroundColor: selected
                          ? COLORS.selectedBackground
                          : "transparent",

                        "&:hover": {
                          backgroundColor: COLORS.selectedBackground,
                        },
                      }}
                    >
                      {item.portName ?? `Port ${item.portIndex}`}
                    </MenuItem>
                  );
                })}
              </CustomMenu>
            </Box>
          </Stack>
        </Stack>

        <Typography
          sx={{
            marginBottom: "12px",

            fontSize: "14px",

            fontWeight: 600,

            color: COLORS.text,
          }}
        >
          Port List (Top 12 by Linkrate)
        </Typography>

        <Box
          sx={{
            height: "585px",

            padding: "10px",

            overflowY: "auto",

            border: `1px solid ${COLORS.border}`,

            borderRadius: "4px",

            backgroundColor: COLORS.softBackground,
          }}
        >
          {loading ? (
            <Box
              sx={{
                height: "180px",

                display: "flex",

                justifyContent: "center",

                alignItems: "center",
              }}
            >
              <CircularProgress size={24} />
            </Box>
          ) : portList.length === 0 ? (
            <Box
              sx={{
                height: "180px",

                display: "flex",

                justifyContent: "center",

                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: "12px",

                  color: COLORS.mutedText,
                }}
              >
                No data available
              </Typography>
            </Box>
          ) : (
            portList.map((item) => {
              const selected =
                selectedPort?.deviceId === item.deviceId &&
                Number(selectedPort?.portIndex) === Number(item.portIndex);

              return (
                <Box
                  key={`${item.deviceId}-${item.portIndex}`}
                  onClick={() => handleSelectPort(item)}
                  sx={{
                    marginBottom: "10px",

                    padding: "14px",

                    border: selected
                      ? `1px solid ${COLORS.primary}`
                      : `1px solid ${COLORS.border}`,

                    borderRadius: "5px",

                    backgroundColor: selected
                      ? COLORS.selectedBackground
                      : "#FFFFFF",

                    cursor: "pointer",

                    transition: "0.15s",

                    "&:hover": {
                      borderColor: COLORS.primary,
                    },
                  }}
                >
                  <Stack direction="row" justifyContent="space-between">
                    <Box>
                      <Typography
                        sx={{
                          fontSize: "13px",

                          fontWeight: 600,

                          color: COLORS.text,
                        }}
                      >
                        {item.switchName ?? "-"}
                      </Typography>

                      <Typography
                        sx={{
                          marginTop: "3px",

                          fontSize: "11px",

                          color: COLORS.mutedText,
                        }}
                      >
                        {item.portName ?? `Port ${item.portIndex}`}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        textAlign: "right",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "11px",

                          color: COLORS.text,
                        }}
                      >
                        Linkrate: {formatLinkRate(item.linkRate)}
                      </Typography>

                      <Typography
                        sx={{
                          marginTop: "3px",

                          fontSize: "11px",

                          color: "#53658F",
                        }}
                      >
                        Utilization: {formatPercent(item.utilizationPercent)}
                      </Typography>
                    </Box>
                  </Stack>

                  <Box
                    sx={{
                      borderTop: `1px solid ${COLORS.border}`,

                      marginY: "10px",
                    }}
                  />

                  <Stack direction="row" spacing={5}>
                    <PortInfo title="Error" value={item.errorCount ?? "-"} />

                    <PortInfo
                      title="PoE"
                      value={formatPoePower(item.poePower)}
                    />
                  </Stack>

                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-end"
                    sx={{
                      marginTop: "12px",
                    }}
                  >
                    <Box
                      sx={{
                        width: "62%",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "11px",

                          marginBottom: "5px",

                          color: COLORS.mutedText,
                        }}
                      >
                        Utilization
                      </Typography>

                      <Box
                        sx={{
                          height: "6px",

                          backgroundColor: "#E7EBF3",

                          borderRadius: "6px",

                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            width: `${Math.min(
                              100,
                              Math.max(0, Number(item.utilizationPercent ?? 0)),
                            )}%`,

                            height: "100%",

                            backgroundColor: COLORS.primary,
                          }}
                        />
                      </Box>
                    </Box>

                    <SolidButton
                      onClick={(event) => {
                        event.stopPropagation();

                        handleSelectPort(item);
                      }}
                      sx={{
                        height: "32px",
                      }}
                    >
                      View
                    </SolidButton>
                  </Stack>
                </Box>
              );
            })
          )}
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,

          minWidth: 0,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            height: "40px",

            marginBottom: "8px",
          }}
        >
          <Typography
            sx={{
              fontSize: "12px",

              color: COLORS.text,
            }}
          >
            {selectedPort
              ? `${selectedPort.switchName ?? "-"} - ${
                  selectedPort.portName ?? `Port ${selectedPort.portIndex}`
                }`
              : "Select a switch port on the left to view details."}
          </Typography>

          <OutlineButton
            onClick={handleClear}
            sx={{
              height: "32px",
            }}
          >
            Clear
          </OutlineButton>
        </Stack>

        <ChartCard title="Active Port vs Throughput">
          <EChart option={activePortOption} height={270} />
        </ChartCard>

        <ChartCard title="Performance — Linkrate">
          <EChart option={performanceOption} height={250} />
        </ChartCard>

        <ChartCard title="Top 5 Ports by Traffic">
          <EChart option={trafficOption} height={190} />
        </ChartCard>
      </Box>
    </Box>
  );
};

const PortInfo = ({ title, value }) => {
  return (
    <Box>
      <Typography
        sx={{
          fontSize: "11px",

          color: COLORS.mutedText,
        }}
      >
        {title}
      </Typography>

      <Typography
        sx={{
          marginTop: "2px",

          fontSize: "13px",

          fontWeight: 600,

          color: COLORS.primary,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

const EChart = ({ option, height = 200 }) => {
  const chartRef = useRef(null);

  useEffect(() => {
    if (!chartRef.current) {
      return;
    }

    let chart = echarts.getInstanceByDom(chartRef.current);

    if (!chart) {
      chart = echarts.init(chartRef.current);
    }

    chart.setOption(option, true);

    const resize = () => {
      chart.resize();
    };

    window.addEventListener("resize", resize);

    return () => {
      window.removeEventListener("resize", resize);

      chart.dispose();
    };
  }, [option]);

  return (
    <Box
      ref={chartRef}
      sx={{
        width: "100%",

        height,
      }}
    />
  );
};

const ChartCard = ({ title, children }) => {
  return (
    <Box
      sx={{
        width: "100%",

        marginBottom: "16px",

        overflow: "hidden",

        border: `1px solid ${COLORS.border}`,

        borderRadius: "6px",

        backgroundColor: "#FFFFFF",
      }}
    >
      <Typography
        sx={{
          paddingTop: "15px",

          paddingX: "16px",

          fontSize: "13px",

          fontWeight: 500,

          color: COLORS.text,
        }}
      >
        {title}
      </Typography>

      {children}
    </Box>
  );
};

export default WiredInsight;
